import { useState, useCallback, useMemo } from 'react';
import { useMatches, useRouter } from '@tanstack/react-router';
import { useCompany } from '../contexts/CompanyContext';
import { homePathFor, type AdminArea } from '../routes/auth';
import { pageToPath, paths, slugFromPathname } from '../routes/paths';
import type { FormMode } from '../routes/router';

export interface PrefillLine {
  itemNo: string;
  description: string;
  quantity: number;
  unitPrice: number;
  weight: number;
  unitOfMeasure: string;
  comment: string;
}

export interface PrefillData {
  shipToCode: string;
  storageType: string;
  /** Ship-from location carried over from the order being reordered. */
  locationCode?: string;
  lines: PrefillLine[];
}

const param = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

/**
 * Page navigation backed by the router (N3): the URL is the source of truth.
 *
 * Same API as before (currentPage, setCurrentPage, handle*), so callers didn't
 * change — but currentPage, record ids, form mode and admin tab are now read
 * from the matched route, and every handler navigates (push), so deep links,
 * refresh and the browser back button all work.
 *
 * Still in memory (by design): the reorder prefill (too big for a URL; lost on
 * refresh, the form opens empty) and the dashboard refresh counter.
 */
export default function useAppNavigation(isSignedIn = false) {
  const router = useRouter();
  const { company } = useCompany();
  const leaf = useMatches({ select: (matches) => matches[matches.length - 1] });

  const [selectedShipToCode, setSelectedShipToCode] = useState<string | null>(null);
  const [prefillOrder, setPrefillOrder] = useState<PrefillData | null>(null);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  const staticData = leaf?.staticData ?? {};
  const params = (leaf?.params ?? {}) as Record<string, unknown>;
  const search = (leaf?.search ?? {}) as Record<string, unknown>;

  const rawPage = staticData.page ?? 'not-found';
  const currentPage = rawPage === 'home' ? (isSignedIn ? 'dashboard' : 'login') : rawPage;
  const formMode: FormMode = staticData.formMode ?? 'order';
  const adminArea: AdminArea | null = staticData.adminArea ?? null;
  const adminTab = adminArea ? (param(params.tab) ?? 'dashboard') : null;
  const adminRecordId = adminArea ? param(params.recordId) : null;

  const editingQuoteNo = param(params.quoteNo);
  const viewingInvoiceNo = param(params.invoiceNo);
  const editingOrderNo = param(params.orderNo) ?? (viewingInvoiceNo ? param(search.order) : null);
  const viewingCreditMemoNo = param(params.creditMemoNo);
  const viewingShipmentNo = param(params.shipmentNo);
  /** UX-GROWTH H2: article slug from /help/{article}. */
  const helpArticle = currentPage === 'help' ? param(params.article) : null;
  /** Legal document slug from /legal/{slug} (privacy policy, terms …). */
  const legalSlug = currentPage === 'legal' ? param(params.docSlug) : null;

  /** Company slug for customer URLs: current URL first, then the resolved company. */
  const slug = useCallback((): string => {
    return param(params.slug)
      ?? slugFromPathname(router.state.location.pathname)
      ?? (company?.slug || 'portal');
  }, [params.slug, router, company?.slug]);

  const go = useCallback((href: string, opts: { replace?: boolean } = {}) => {
    void router.navigate({ href, replace: opts.replace });
  }, [router]);

  const setCurrentPage = useCallback((page: string) => {
    if (page === 'order-form') {setPrefillOrder(null);}
    go(pageToPath(page, slug()));
  }, [go, slug]);

  const handleNewOrder = useCallback((prefillData: PrefillData | null = null) => {
    setPrefillOrder(prefillData);
    go(paths.newOrder(slug()));
  }, [go, slug]);

  const handleEditOrder = useCallback((orderNo: string) => {
    setPrefillOrder(null);
    go(paths.order(slug(), orderNo));
  }, [go, slug]);

  const handleViewPostedInvoice = useCallback((invoiceNo: string, orderNo?: string) => {
    setPrefillOrder(null);
    go(paths.invoice(slug(), invoiceNo, orderNo));
  }, [go, slug]);

  const handleViewCreditMemo = useCallback((creditMemoNo: string) => {
    setPrefillOrder(null);
    go(paths.creditMemo(slug(), creditMemoNo));
  }, [go, slug]);

  const handleViewShipment = useCallback((shipmentNo: string) => {
    setPrefillOrder(null);
    go(paths.shipment(slug(), shipmentNo));
  }, [go, slug]);

  const handleNewQuote = useCallback(() => {
    setPrefillOrder(null);
    go(paths.newQuote(slug()));
  }, [go, slug]);

  const handleEditQuote = useCallback((quoteNo: string) => {
    setPrefillOrder(null);
    go(paths.quote(slug(), quoteNo));
  }, [go, slug]);

  const handleQuoteConverted = useCallback(() => {
    setDashboardRefreshKey(prev => prev + 1);
    go(paths.home());
  }, [go]);

  const handleViewMyAccount = useCallback(() => {
    go(paths.myAccount(slug()));
  }, [go, slug]);

  const handleBackToDashboard = useCallback(() => {
    setSelectedShipToCode(null);
    setPrefillOrder(null);
    setDashboardRefreshKey(prev => prev + 1);
    go(paths.home());
  }, [go]);

  const refreshDashboard = useCallback(() => {
    setDashboardRefreshKey(prev => prev + 1);
  }, []);

  /** Navigate to the user's home page based on their role */
  const handleNavigateHome = useCallback((role?: string) => {
    setSelectedShipToCode(null);
    setPrefillOrder(null);
    if (!role || role === 'user') {setDashboardRefreshKey(prev => prev + 1);}
    go(homePathFor(role ? ({ role } as never) : null));
  }, [go]);

  /** Admin shells: tab changes are history entries (back returns to the previous tab). */
  // ADMIN-REVIEW X7: an optional query string opens a list already filtered (listUrlState format,
  // e.g. "f.role=tenant_admin"), so dashboard cards deep-link to what they count.
  const handleAdminTabChange = useCallback((tab: string, search?: string) => {
    if (adminArea) {go(paths.admin(adminArea, tab) + (search ? `?${search.replace(/^\?/, '')}` : ''));}
  }, [go, adminArea]);

  /** ADMIN-REVIEW X1: open a record's detail page in the current admin shell (history entry). */
  const openAdminRecord = useCallback((tab: string, id: string, search?: string) => {
    if (adminArea) {go(paths.adminRecord(adminArea, tab, id) + (search ? `?${search.replace(/^\?/, '')}` : ''));}
  }, [go, adminArea]);

  /** Go to any in-app path (e.g. '/tntadm/billing', or a quick-find result). */
  const goTo = useCallback((href: string) => {
    setPrefillOrder(null);
    go(href);
  }, [go]);
  const companySlug = slug();

  return useMemo(() => ({
    currentPage,
    setCurrentPage,
    goTo,
    companySlug,
    editingOrderNo,
    editingQuoteNo,
    viewingInvoiceNo,
    viewingCreditMemoNo,
    viewingShipmentNo,
    selectedShipToCode,
    formMode,
    prefillOrder,
    dashboardRefreshKey,
    adminTab,
    adminRecordId,
    helpArticle,
    legalSlug,
    handleAdminTabChange,
    openAdminRecord,
    refreshDashboard,
    handleNewOrder,
    handleNewQuote,
    handleEditOrder,
    handleEditQuote,
    handleQuoteConverted,
    handleViewPostedInvoice,
    handleViewCreditMemo,
    handleViewShipment,
    handleViewMyAccount,
    handleBackToDashboard,
    handleNavigateHome,
  }), [currentPage, setCurrentPage, goTo, companySlug, editingOrderNo, editingQuoteNo, viewingInvoiceNo, viewingCreditMemoNo,
    viewingShipmentNo, selectedShipToCode, formMode, prefillOrder, dashboardRefreshKey, adminTab, adminRecordId, helpArticle, legalSlug, handleAdminTabChange, openAdminRecord,
    refreshDashboard, handleNewOrder, handleNewQuote, handleEditOrder, handleEditQuote,
    handleQuoteConverted, handleViewPostedInvoice, handleViewCreditMemo, handleViewShipment, handleViewMyAccount,
    handleBackToDashboard, handleNavigateHome]);
}
